import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Button, Card, Chip, EmptyState, Modal, Spinner } from "../../components/ui";
import { BellIcon, FilterIcon, SortIcon } from "../../components/ui/icons";
import {
  BOX_COUNT,
  EVENT_TYPES,
  allBoxesEnabled,
  allTypesEnabled,
  boxNumber,
  countActiveFilters,
  eventType,
  filterAndSort,
} from "../../utilities/notifications";
import { areEqualDates, getNotificationDateString, getTimeString } from "../../utilities/utils";

const TONE_DOT = {
  good: "bg-teal-500",
  warning: "bg-amber-500",
  critical: "bg-red-500",
  neutral: "bg-gray-300",
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [deviceTimezone, setDeviceTimezone] = useState(null);
  const [nextPageInfo, setNextPageInfo] = useState({ token: null, filter: null });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [types, setTypes] = useState(allTypesEnabled);
  const [boxes, setBoxes] = useState(allBoxesEnabled);
  const [descending, setDescending] = useState(true);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  // Draft state so Cancel discards, matching the old modals.
  const [draftTypes, setDraftTypes] = useState(types);
  const [draftBoxes, setDraftBoxes] = useState(boxes);
  const [draftDescending, setDraftDescending] = useState(descending);

  const { deviceId } = useParams();

  const getNotifications = useCallback(
    async (token, filter) => {
      setIsLoading(true);
      try {
        const query = token ? `?token=${token}&filter=${filter}` : "";
        const { data } = await axios.get(`/app-api/device/${deviceId}/notification${query}`);
        if (data) {
          setNotifications((prev) => (token ? [...prev, ...data.notifications] : data.notifications));
          setDeviceTimezone(data.deviceTimezone);
          setNextPageInfo({
            token: data.nextPageInfo?.nextPageToken || null,
            filter: data.nextPageInfo?.pagefilter || null,
          });
        }
      } catch (err) {
        console.error(err);
        setError(err.response?.data || "Could not load notifications");
      } finally {
        setIsLoading(false);
      }
    },
    [deviceId]
  );

  useEffect(() => {
    getNotifications();
  }, [getNotifications]);

  const displayed = useMemo(
    () => filterAndSort(notifications, { types, boxes, descending }),
    [notifications, types, boxes, descending]
  );

  const activeFilters = countActiveFilters({ types, boxes });

  const openFilter = () => {
    setDraftTypes(types);
    setDraftBoxes(boxes);
    setIsFilterOpen(true);
  };

  const openSort = () => {
    setDraftDescending(descending);
    setIsSortOpen(true);
  };

  const applyFilter = () => {
    setTypes(draftTypes);
    setBoxes(draftBoxes);
    setIsFilterOpen(false);
  };

  const clearFilter = () => {
    setTypes(allTypesEnabled());
    setBoxes(allBoxesEnabled());
    setIsFilterOpen(false);
  };

  const zone = deviceTimezone?.timezoneValue;

  return (
    <>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-gray-500">
          {displayed.length} shown
          {activeFilters > 0 && ` · ${activeFilters} filter${activeFilters > 1 ? "s" : ""} applied`}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={openFilter}>
            <FilterIcon className="h-4 w-4" />
            Filter
            {activeFilters > 0 && (
              <span className="ml-1 rounded-full bg-primary-600 px-1.5 text-[10px] font-bold text-white">
                {activeFilters}
              </span>
            )}
          </Button>
          <Button variant="secondary" size="sm" onClick={openSort}>
            <SortIcon className="h-4 w-4" />
            {descending ? "Newest" : "Oldest"}
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        {error && <p className="px-5 py-4 text-sm text-red-600">{error}</p>}

        {!error && displayed.length === 0 && !isLoading && (
          <EmptyState
            icon={BellIcon}
            title={notifications.length ? "Nothing matches these filters" : "No notifications yet"}
            description={
              notifications.length
                ? "Try turning some event types or compartments back on."
                : "Dose events from the device will appear here."
            }
            action={
              notifications.length ? (
                <Button variant="secondary" size="sm" onClick={clearFilter}>
                  Clear filters
                </Button>
              ) : null
            }
          />
        )}

        <div className="divide-y divide-gray-100">
          {displayed.map((notification, index) => {
            const previous = displayed[index - 1];
            const newDay =
              index === 0 ||
              !areEqualDates([previous.timestamp, notification.timestamp], zone);
            const type = eventType(notification);
            const box = boxNumber(notification);
            return (
              <div key={`${notification.timestamp}-${notification.code}-${index}`}>
                {newDay && (
                  <p className="bg-[#fbfcfd] px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-gray-500">
                    {getNotificationDateString(notification.timestamp, zone)}
                  </p>
                )}
                {/* Stacked layout on a phone, single row from `nav` up. */}
                <div className="flex items-start gap-3 px-5 py-3.5">
                  <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${TONE_DOT[type.tone]}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-primary-900">{notification.message}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500">
                      <span className="font-mono">{getTimeString(notification.timestamp, zone)}</span>
                      <span aria-hidden="true">·</span>
                      <span>{type.label}</span>
                      {box && (
                        <>
                          <span aria-hidden="true">·</span>
                          <span>Box {box}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {isLoading && (
          <div className="flex justify-center py-6 text-primary-600">
            <Spinner />
          </div>
        )}

        {!isLoading && nextPageInfo.token && (
          <div className="border-t border-gray-100 px-5 py-4 text-center">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => getNotifications(nextPageInfo.token, nextPageInfo.filter)}
            >
              Show more
            </Button>
          </div>
        )}
      </Card>

      <Modal
        isOpen={isFilterOpen}
        onRequestClose={() => setIsFilterOpen(false)}
        title="Filter By"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={clearFilter}>
              Clear all
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setIsFilterOpen(false)}>
              Cancel
            </Button>
            <Button onClick={applyFilter}>Apply</Button>
          </>
        }
      >
        <p className="field-label">Event type</p>
        <div className="mb-5 space-y-1">
          {EVENT_TYPES.map((type) => (
            <CheckRow
              key={type.id}
              checked={draftTypes[type.id]}
              onChange={(checked) => setDraftTypes((prev) => ({ ...prev, [type.id]: checked }))}
              label={type.label}
            >
              <Chip tone={type.tone}>{type.label}</Chip>
            </CheckRow>
          ))}
        </div>

        <p className="field-label">Compartment</p>
        <div className="grid grid-cols-2 gap-1 sm:grid-cols-4">
          {Array.from({ length: BOX_COUNT }, (_, i) => i + 1).map((box) => (
            <CheckRow
              key={box}
              checked={draftBoxes[box]}
              onChange={(checked) => setDraftBoxes((prev) => ({ ...prev, [box]: checked }))}
              label={`Box ${box}`}
            >
              <span className="text-sm text-primary-900">Box {box}</span>
            </CheckRow>
          ))}
        </div>
      </Modal>

      <Modal
        isOpen={isSortOpen}
        onRequestClose={() => setIsSortOpen(false)}
        title="Sort By"
        size="sm"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setIsSortOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setDescending(draftDescending);
                setIsSortOpen(false);
              }}
            >
              Apply
            </Button>
          </>
        }
      >
        <p className="field-label">Notification time</p>
        <div className="space-y-2">
          {[
            { value: true, label: "Newest to oldest" },
            { value: false, label: "Oldest to newest" },
          ].map((option) => (
            <label
              key={String(option.value)}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition ${
                draftDescending === option.value
                  ? "border-primary-500 bg-primary-50"
                  : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              <input
                type="radio"
                name="sort-order"
                checked={draftDescending === option.value}
                onChange={() => setDraftDescending(option.value)}
              />
              <span className="text-sm text-primary-900">{option.label}</span>
            </label>
          ))}
        </div>
      </Modal>
    </>
  );
};

const CheckRow = ({ checked, onChange, label, children }) => (
  <label className="flex cursor-pointer items-center gap-3 rounded-lg px-1 py-2 transition hover:bg-gray-50">
    <input
      type="checkbox"
      checked={checked}
      aria-label={label}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
    />
    {children}
  </label>
);

export default Notifications;
